import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { get, post } from "@/lib/api";
import { PageLoader, PageHeader, EmptyState, Badge } from "@/components/common";
import { FileText, Clock, ListChecks, Trophy } from "lucide-react";

export default function TestCatalog({ type, title }) {
  const nav = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["tests", type], queryFn: () => get(`/tests?test_type=${type}`) });

  const start = async (tid) => {
    try { const r = await post(`/tests/${tid}/start`); nav(`/exam/${r.attempt_id}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Purchase required or unavailable"); }
  };

  return (
    <div>
      <PageHeader title={title} subtitle="Attempt tests in a real exam environment" />
      {isLoading ? <PageLoader /> : !data || data.length === 0 ? <EmptyState icon={FileText} title="No tests available yet" hint="Check back soon — new tests are added regularly." /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.map((t) => (
            <div key={t.id} className="bg-white rounded-2xl border border-slate-200 p-5 card-hover" data-testid={`test-card-${t.id}`}>
              <div className="flex items-center justify-between mb-2"><Badge tone="indigo">{t.test_type}</Badge>{t.is_free ? <Badge tone="green">Free</Badge> : <Badge tone="amber">₹{t.price}</Badge>}</div>
              <h3 className="font-display font-bold text-slate-900 leading-snug">{t.title}</h3>
              <div className="flex flex-wrap gap-3 text-xs text-slate-500 mt-3">
                <span className="flex items-center gap-1"><ListChecks className="h-3.5 w-3.5" /> {t.total_questions} Qs</span>
                <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {t.duration_min} min</span>
                <span className="flex items-center gap-1"><Trophy className="h-3.5 w-3.5" /> {t.total_marks} marks</span>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={() => start(t.id)} data-testid={`start-test-${t.id}`} className="flex-1 bg-indigo-800 hover:bg-indigo-900 text-white text-sm font-semibold py-2 rounded-lg">Start Test</button>
                <button onClick={() => nav(`/app/tests`) || post} className="hidden" />
              </div>
              <p className="text-[11px] text-slate-400 mt-2">{t.attempts_count} attempts</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
